import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import ListFilterBar from './components/ListFilterBar.js';
import { isWarehouseUser } from './accessControl.js';
import StatusBadge from './components/StatusBadge.js';

const QualityCheckList = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch MOCs where DC is confirmed, QC is passed, or already dispatched for audit
    const q = query(
      collection(db, "dispatches"),
      where("dispatchStatus", "in", ["invoice_ready", "ewaybill_complete", "dc_confirmed", "qc_passed", "dispatched"]),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const mocList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching QC Queue: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.warehouse).filter(Boolean)))], [mocs]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.dispatchStatus).filter(Boolean)))], [mocs]);
  const typeOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.typeOfMOC).filter(Boolean)))], [mocs]);
  const filterFields = useMemo(() => ([
    {
      key: 'searchTerm',
      type: 'search',
      label: 'Customer Search',
      placeholder: 'Search by Customer, MOC No, DC No, Order No or Transfer No...',
      minWidth: '260px'
    },
    {
      key: 'filterType',
      type: 'select',
      label: 'MOC Type',
      options: typeOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Types' : option
      }))
    },
    {
      key: 'filterStatus',
      type: 'select',
      label: 'Status',
      options: statusOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Statuses' : option
      }))
    },
    {
      key: 'filterWarehouse',
      type: 'select',
      label: 'Warehouse',
      options: warehouseOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Warehouses' : option
      }))
    }
  ]), [typeOptions, statusOptions, warehouseOptions]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => {
      if (moc.dispatchStatus === 'delivered') return false;
      const matchesSearch = (
        moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.challanNo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesWarehouse = filterWarehouse === 'All' || moc.warehouse === filterWarehouse;
      const matchesStatus = filterStatus === 'All' || moc.dispatchStatus === filterStatus;
      const matchesType = filterType === 'All' || moc.typeOfMOC === filterType;
      return matchesSearch && matchesWarehouse && matchesStatus && matchesType;
    });
  }, [mocs, searchTerm, filterWarehouse, filterStatus, filterType]);

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const qcPendingBadgeMeta = {
    label: 'QC Pending',
    colors: {
      backgroundColor: '#eef2ff',
      color: '#4338ca',
      borderColor: '#c7d2fe'
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'invoice_ready':
      case 'ewaybill_complete':
      case 'dc_confirmed':
        return <StatusBadge meta={qcPendingBadgeMeta} iconSize={12} />;
      case 'qc_passed':
        return <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 'bold' }}>QC Passed</div>;
      case 'dispatched':
        return <StatusBadge status="dispatched" iconSize={12} />;
      default:
        return <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>{status?.replace(/_/g, ' ')?.toUpperCase()}</div>;
    }
  };

  const handleRowClick = (moc) => {
    navigate(`/ops/qc/${moc.id}`);
  };

  const getActionConfig = (moc) => {
    if (isWarehouseUser(user) && ['invoice_ready', 'ewaybill_complete'].includes(moc.dispatchStatus)) {
      return {
        label: 'Edit QC',
        style: editQcBtnStyle,
        onClick: () => navigate(`/ops/qc/${moc.id}`, { state: { mode: 'edit' } })
      };
    }

    return {
      label: 'Preview',
      style: previewBtnStyle,
      onClick: () => navigate(`/ops/qc/${moc.id}`)
    };
  };

  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') setSearchTerm(value);
    if (key === 'filterType') setFilterType(value);
    if (key === 'filterStatus') setFilterStatus(value);
    if (key === 'filterWarehouse') setFilterWarehouse(value);
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Quality Check (MOC Gate)</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Verify material counts, record vehicle details, and confirm final exit.</p>
        </div>
      </div>

      <ListFilterBar
        fields={filterFields}
        values={{ searchTerm, filterType, filterStatus, filterWarehouse }}
        onFieldChange={handleFilterChange}
      />

      {mocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <UserCheck size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No material waiting for Quality Check!</h3>
          <p style={{ color: '#64748b' }}>Items appear here once the Delivery Challan is confirmed.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Customer</th>
                <th style={thStyle}>Order No./Transfer No.</th>
                <th style={thStyle}>DC Number</th>
                <th style={thStyle}>MOC Number</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => {
                const actionConfig = getActionConfig(moc);

                return (
                  <tr
                    key={moc.id}
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                    onClick={() => handleRowClick(moc)}
                    className="hover-highlight"
                  >
                    <td style={tdStyle}>{formatDate(moc.updatedAt?.toDate ? moc.updatedAt.toDate() : moc.updatedAt)}</td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{moc.customerName}</div>
                    </td>
                    <td style={tdStyle}>{moc.typeOfMOC === 'Interbranch Transfer' ? (moc.poNumber || '---') : (moc.orderNumber || '---')}</td>
                    <td style={tdStyle}>{moc.challanNo || '---'}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{moc.mocNo}</div>
                    </td>
                    <td style={tdStyle}>{moc.warehouse}</td>
                    <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          actionConfig.onClick();
                        }}
                        style={actionConfig.style}
                      >
                        {actionConfig.label}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const previewBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer' };
const editQcBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };

export default QualityCheckList;
